import React, { useMemo } from 'react';
import Button from '../Button/Button';
import ButtonGroup, {
  Props as ButtonGroupProps,
} from '../ButtonGroup/ButtonGroup';
import { OnChangeEvent, ToggleButtonGroupEvent } from '../events/EventProvider';
import useEvents from '../events/useEvents';
import useTheme from '../style/useTheme';

export type ToggleButtonGroupOption = {
  label: string;
  value: string;
};

export type Props = {
  eventId?: string;
  options: ToggleButtonGroupOption[];
  value?: ToggleButtonGroupOption | null;
  role?: React.AriaRole;
  onChange:
    | ((value: ToggleButtonGroupOption) => void)
    | ((value: ToggleButtonGroupOption | null) => void);
  disabledValue?: string;
  includeBorder?: boolean;
} & ButtonGroupProps;

const ToggleButtonGroup: React.FC<Props> = ({
  options,
  eventId,
  value,
  role = 'group',
  onChange,
  disabledValue,
  includeBorder = true,
  ...props
}) => {
  const { resolveColor, theme } = useTheme();

  const triggerEvent = useEvents<OnChangeEvent<ToggleButtonGroupEvent>>(
    eventId,
    'ToggleButtonGroup'
  );

  const buttonRole = useMemo(
    () => (role === 'tablist' ? 'tab' : undefined),
    [role]
  );

  return (
    <ButtonGroup weight="normal" {...props} role={role}>
      {options.map((option) => {
        const isDisabled = disabledValue === option.value;
        const isSelected = value && value.value === option.value;

        return (
          <Button
            // Make font size 12 to accomodate amt selection screen on mobile browsers
            // TODO: Should we make this responsive eventually?
            sx={{
              color: {
                type: 'component',
                value: isSelected
                  ? theme.components.buttonGroup.toggle.selectedOnColor
                  : theme.components.buttonGroup.toggle.baseOnColor,
              },
              fontSize: '12px',
              boxShadow: 'none',
              border: includeBorder
                ? `1px solid ${resolveColor({
                    type: 'theme',
                    colorLegacy: 'Neutral/30',
                    colorNext: 'neutral/default',
                  })}`
                : 'none',
              '&:disabled': {
                backgroundColor: {
                  type: 'component',
                  value: theme.components.buttonGroup.toggle.disabledColor,
                },
                cursor: 'default',
                color: {
                  type: 'component',
                  value: theme.components.buttonGroup.toggle.disabledOnColor,
                },
              },
            }}
            backgroundColor={{
              type: 'component',
              value: isSelected
                ? theme.components.buttonGroup.toggle.selectedColor
                : theme.components.buttonGroup.toggle.baseColor,
            }}
            skipThemeSx
            aria-label={option.label}
            key={option.value}
            data-testid={`ToggleButtonGroupOption-${option.value}`}
            variant={isSelected ? 'solid' : 'outline'}
            color={isSelected ? 'primary' : 'neutral'}
            onClick={() => {
              onChange(option);
              triggerEvent({ selected: option });
            }}
            aria-selected={!!(value && value.value === option.value)}
            type="button"
            role={buttonRole}
            disabled={isDisabled}
          >
            {option.label}
          </Button>
        );
      })}
    </ButtonGroup>
  );
};

export default ToggleButtonGroup;
